import { useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";
import { utils } from "@atlaskit/util-service-support";

import NotificationError from "./notification-error";
import NotificationFeed from "./notification-feed";
import NotificationSkeleton from "./notification-skeleton";
import { groupNotificationsByTime } from "./notifications-time-grouping";
import { NotificationGroup as NotificationGroupType } from "./types";

interface NotificationsResponse {
    groups: NotificationGroupType[];
}

interface NotificationsDrawerProps {
    notificationLogUrl: string;
}

const headerStyles = css({
    "fontFamily":
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    "margin": 0,
    "padding": "24px 16px 16px 24px",
    "position": "relative",
    "&::after": {
        content: "''",
        position: "absolute",
        insetBlockEnd: 0,
        insetInlineStart: token("space.300"),
        insetInlineEnd: token("space.300"),
        height: "2px",
        backgroundColor: token("color.border")
    }
});

const drawerContainerStyles = css({
    height: "calc(-100px + 100vh)",
    width: "540px",
    maxWidth: "100vw",
    overflowY: "auto"
});

const NotificationsDrawer = ({
    notificationLogUrl
}: NotificationsDrawerProps) => {
    const [groups, setGroups] = useState<NotificationGroupType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const response =
                    await utils.requestService<NotificationsResponse>(
                        {
                            url: `${notificationLogUrl}/api/3/notification-groups`
                        },
                        {
                            queryParams: {
                                category: "direct",
                                expand: "content.body",
                                limit: 8
                            },
                            requestInit: {
                                credentials: "include",
                                headers: {
                                    "content-type": "application/json",
                                    "Accept-Language": "en",
                                    "x-app-name": "wac-notifications",
                                    "x-app-version": "local"
                                }
                            }
                        }
                    );

                setGroups(response.groups || []);
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to fetch notifications"
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotifications();
    }, [notificationLogUrl]);

    const groupedNotifications = useMemo(() => {
        // Group notification groups by time, preserving group metadata (additionalActors)
        return groupNotificationsByTime(groups);
    }, [groups]);

    const renderContent = () => {
        if (isLoading) {
            return (
                <div>
                    {[...Array(5)].map((_, index) => (
                        <NotificationSkeleton key={index} />
                    ))}
                </div>
            );
        }

        if (error) {
            return <NotificationError />;
        }

        if (groups.length === 0) {
            return <div>No notifications</div>;
        }

        return <NotificationFeed groupedNotifications={groupedNotifications} />;
    };

    return (
        <div css={drawerContainerStyles}>
            <h2 css={headerStyles}>Notifications</h2>
            {renderContent()}
        </div>
    );
};

export { NotificationsDrawer };
